import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Dialog } from '@syncfusion/ej2-popups';
import { Subscription } from 'rxjs';
import { getBackButtonPopstateHandler, installBackButtonPopstateListener, setBackButtonPopstateHandler } from './back-button-popstate';

// =============================================================================
// BACK BUTTON CLOSES THE TOPMOST OVERLAY (touch devices)
//
// On a phone the Android back button / gesture is how people dismiss things.
// Before this service it left the page instead: Rob opened Voice to Note in a
// notebook, pressed back to close it, and landed on the Console with the
// notebook (and the dialog's transcript) gone.
//
// HOW. Every open overlay owns one same-URL history entry, pushed with
// `history.pushState`. Entries are stamped (BACK_STATE_KEY depth, a page token
// and a sequence number), and the page entry under them is stamped too, so a
// popstate can be matched exactly to the entry it landed on. The router never
// sees our entries:
//   - pushState does not notify Angular's Location, so nothing routes on push;
//   - our popstate listener is registered in polyfills.ts, before any vendor
//     or app module, so it runs before Angular's Location listener (see
//     back-button-popstate.ts), and calls stopImmediatePropagation for every
//     pop that is ours. The router, and MatDialog's closeOnNavigation, never
//     hear about it.
//
// The service keeps one invariant: the depth of the current history entry
// equals the number of open overlays. sync() restores it whenever it drifts:
//   - overlay opened             -> push an entry, ONLY with user activation
//                                   (see canPush); otherwise on the next tap
//   - overlay closed by X/Save   -> history.go(-1) after a short grace (a
//                                   dialog opened next reuses the entry)
//   - back pressed               -> the browser already popped; close the
//                                   topmost overlay through its own close path
//   - close refused (mid-save,
//     PIN dialog, confirm shown) -> history.go(+1) back onto the entry we came
//                                   from, so nothing new is pushed
//   - forward onto a stale entry -> keep going forward if there is more, else
//                                   step back to the right depth
//   - reload onto an entry       -> step back to the page entry
//   - route change               -> wait for NavigationEnd, then re-sync
//   - a router navigation while our own go() is queued -> step forward again
//     so the user's navigation is never undone
//
// Only enabled on a coarse primary pointer (phones, tablets). See detect().
// =============================================================================

/** history.state key carrying the overlay depth of an entry we pushed. */
export const BACK_STATE_KEY = 'fnOverlayDepth';
/** history.state key: which page instance stamped the entry. */
export const BACK_PAGE_KEY = 'fnBackPage';
/** history.state key: unique, increasing per stamped entry. */
export const BACK_SEQ_KEY = 'fnBackSeq';

/** localStorage override for testing on a desktop: 'on' or 'off'. */
export const BACK_FORCE_KEY = 'fn.backClosesOverlay';

/**
 * What an overlay did when back was pressed.
 * - closed : it is closing now; its entry is dropped.
 * - kept   : it stays open (non-dismissible, save in flight, confirm shown).
 * - pending: it was asked to close through code that runs on change detection
 *            (an Escape handler, an *ngIf); checked again after a tick.
 */
export type BackOutcome = 'closed' | 'kept' | 'pending';
export type BackHandler = () => BackOutcome;

export interface BackEntryHandle {
  /** Call when the overlay closes by any other means. Safe to call twice. */
  release(): void;
  readonly active: boolean;
}

const NOOP_HANDLE: BackEntryHandle = { release: () => undefined, active: false };

/** Syncfusion's own value for dlgClosedBy when the X is clicked. */
const SF_CLOSED_BY_CLOSE_ICON = 'close icon';

/** Attribute on an <ejs-dialog> host: "ignore" or "close" overrides the default. */
export const SF_BACK_ATTR = 'data-back-button';

/** How long to wait for a traversal's popstate before re-checking. */
const TRAVERSAL_TIMEOUT_MS = 1000;
/** A traversal that has not moved after this many timeouts is abandoned, never repeated. */
const TRAVERSAL_MAX_WAITS = 4;
/** Probing forward past a stale entry: if nothing is there, give up fast. */
const PROBE_TIMEOUT_MS = 300;
/** A displaced traversal of ours must land within this window to be undone. */
const ORPHAN_WINDOW_MS = 1500;

/**
 * How long a closed overlay's entry is kept before it is popped. Flows that
 * close one dialog and open the next (Notebook Options -> Download) reuse the
 * entry instead of popping and pushing. A back press inside the grace simply
 * does the cleanup for us.
 */
const RELEASE_GRACE_MS = 250;

/** Input events that grant user activation (HTML "activation triggering input events"). */
const ACTIVATION_EVENTS = ['pointerup', 'touchend', 'mousedown', 'keydown'];

interface Stamp {
  depth: number;
  page: string | null;
  seq: number | null;
}

export function overlayDepth(state: unknown): number {
  if (!state || typeof state !== 'object') {
    return 0;
  }
  const value = (state as Record<string, unknown>)[BACK_STATE_KEY];
  return typeof value === 'number' && value > 0 ? Math.floor(value) : 0;
}

function stampOf(state: unknown): Stamp {
  const s = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  const page = typeof s[BACK_PAGE_KEY] === 'string' ? (s[BACK_PAGE_KEY] as string) : null;
  const seq = typeof s[BACK_SEQ_KEY] === 'number' ? (s[BACK_SEQ_KEY] as number) : null;
  return { depth: overlayDepth(state), page, seq };
}

/** The router's own state, without our keys. */
function baseState(): Record<string, unknown> {
  const s = history.state && typeof history.state === 'object' ? { ...(history.state as Record<string, unknown>) } : {};
  delete s[BACK_STATE_KEY];
  delete s[BACK_PAGE_KEY];
  delete s[BACK_SEQ_KEY];
  return s;
}

interface Entry {
  readonly id: number;
  readonly label: string;
  readonly onBack: BackHandler;
  readonly isAlive?: () => boolean;
  released: boolean;
}

type TraversalKind = 'sync' | 'restore' | 'probe' | 'compensate';

interface Traversal {
  kind: TraversalKind;
  delta: number;
  /** The entry we expect to land on; null = accept wherever it lands. */
  expect: Stamp | null;
  from: Stamp;
  fromHref: string;
  waits: number;
  /** probe only: where to go if there is nothing forward. */
  fallback: number;
  /** A router navigation started while this traversal was queued. */
  hijacked: boolean;
  timer: ReturnType<typeof setTimeout> | null;
}

/** Holds the one service that the Syncfusion / MatDialog prototype hooks report to. */
let activeService: BackButtonDialogService | null = null;

@Injectable({ providedIn: 'root' })
export class BackButtonDialogService implements OnDestroy {
  readonly enabled: boolean;

  private stack: Entry[] = [];
  private nextId = 1;
  /** Starts at the clock so a reloaded page never reuses a sequence still in its history. */
  private seqCounter = Date.now();
  /** Token of the page instance our entries belong to (null until the first push on a page). */
  private page: string | null = null;
  /** Known sequence numbers of this page's entries, index = depth (0 = page entry). */
  private seqs: Array<number | undefined> = [];
  private traversal: Traversal | null = null;
  private orphan: { delta: number; until: number } | null = null;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private resolving = 0;
  private navigating = false;
  private gestureArmed = false;
  private inGesture = false;
  private routerSub: Subscription | null = null;
  private readonly sfHandles = new WeakMap<object, BackEntryHandle>();
  private readonly sfHandlers = new WeakMap<object, BackHandler>();

  constructor(private zone: NgZone, private router: Router) {
    this.enabled = BackButtonDialogService.detect();
    if (!this.enabled) {
      return;
    }
    activeService = this;
    installSyncfusionHook();
    installMatDialogHook();
    installBackButtonPopstateListener(); // no-op: polyfills.ts already did it
    setBackButtonPopstateHandler(this.onPopState);
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigating = true;
        if (this.traversal && event.navigationTrigger !== 'popstate') {
          // The router will push its entry before our queued go() runs, and
          // the go() would then walk the user off the page they asked for.
          this.traversal.hijacked = true;
        }
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.navigating = false;
        // After the tick: a routed-away Syncfusion dialog destroys itself on a
        // setTimeout, and it must be gone before we count what is still open.
        setTimeout(() => this.sync(), 0);
      }
    });
    // A reload restores the entry we were on, so the page can start on depth > 0
    // with nothing open. The first sync walks back to the real page entry.
    setTimeout(() => this.sync(), 0);
  }

  /**
   * Touch-first devices only. A mouse user closes dialogs with X or Escape and
   * expects the browser's Back to mean "previous page"; adding entries there
   * would change a working desktop behaviour for no gain. Coarse primary
   * pointer = phones and tablets; a touch laptop keeps desktop behaviour.
   */
  static detect(win: Window = window): boolean {
    if (typeof win === 'undefined' || !win.history || typeof win.history.pushState !== 'function') {
      return false;
    }
    try {
      const forced = win.localStorage.getItem(BACK_FORCE_KEY);
      if (forced === 'on') {
        return true;
      }
      if (forced === 'off') {
        return false;
      }
    } catch {
      // Storage blocked: fall through to the media query.
    }
    return typeof win.matchMedia === 'function' && win.matchMedia('(pointer: coarse)').matches;
  }

  // ---------------------------------------------------------------- public API

  /** Number of overlays currently registered. */
  get openCount(): number {
    this.prune();
    return this.stack.length;
  }

  /**
   * Register an overlay that just opened. `onBack` is called when back is
   * pressed while it is the topmost overlay. Release the handle when it closes.
   */
  register(onBack: BackHandler, label = 'overlay', isAlive?: () => boolean): BackEntryHandle {
    if (!this.enabled) {
      return NOOP_HANDLE;
    }
    const entry: Entry = { id: this.nextId++, label, onBack, isAlive, released: false };
    this.stack.push(entry);
    this.sync();
    return {
      release: () => this.release(entry),
      get active() {
        return !entry.released;
      },
    };
  }

  /** An open/closed flag that registers and releases one entry as it changes. */
  slot(onBack: BackHandler, label = 'overlay'): BackSlot {
    return new BackSlot(this, onBack, label);
  }

  /**
   * Replace the default "same as the X" back behaviour of one Syncfusion dialog,
   * e.g. a wizard where back means "previous step". Pass the DialogComponent.
   */
  setSyncfusionHandler(dialog: object | null | undefined, handler: BackHandler | null): void {
    if (!dialog) {
      return;
    }
    if (handler) {
      this.sfHandlers.set(dialog, handler);
    } else {
      this.sfHandlers.delete(dialog);
    }
  }

  /** The default Syncfusion back behaviour, for custom handlers to fall back on. */
  closeSyncfusionLikeCloseIcon(dialog: any): BackOutcome {
    const el: HTMLElement | null = dialog?.element ?? null;
    const mode = el?.getAttribute(SF_BACK_ATTR);
    if (mode === 'ignore') {
      return 'kept';
    }
    // No X means the dialog is not dismissible by design (the PIN dialog, the
    // inactivity lock, confirmations that need an answer). Back is swallowed.
    if (!dialog.showCloseIcon && mode !== 'close') {
      return 'kept';
    }
    dialog.dlgClosedBy = SF_CLOSED_BY_CLOSE_ICON;
    dialog.hide(new Event('fnbackbutton'));
    return dialog.dialogOpen ? 'kept' : 'closed';
  }

  ngOnDestroy(): void {
    if (!this.enabled) {
      return;
    }
    if (getBackButtonPopstateHandler() === this.onPopState) {
      setBackButtonPopstateHandler(null);
    }
    this.routerSub?.unsubscribe();
    this.disarmGesture();
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
    }
    if (this.traversal?.timer) {
      clearTimeout(this.traversal.timer);
    }
    this.traversal = null;
    if (activeService === this) {
      activeService = null;
    }
  }

  // ------------------------------------------------- dialog-system adapters

  /** @internal Syncfusion Dialog finished opening. */
  onSyncfusionOpened(dialog: any): void {
    if (this.sfHandles.has(dialog)) {
      return;
    }
    const id = dialog?.element?.id ? `#${dialog.element.id}` : '';
    const handle = this.register(
      () => (this.sfHandlers.get(dialog) ?? (() => this.closeSyncfusionLikeCloseIcon(dialog)))(),
      `ejs-dialog${id}`,
      () => !!dialog.dialogOpen && !!dialog.element && dialog.element.isConnected !== false
    );
    this.sfHandles.set(dialog, handle);
  }

  /** @internal Syncfusion Dialog closed or was destroyed while open. */
  onSyncfusionClosed(dialog: any): void {
    const handle = this.sfHandles.get(dialog);
    if (handle) {
      this.sfHandles.delete(dialog);
      handle.release();
    }
  }

  /** @internal MatDialog.open() returned a ref. */
  trackMatDialog(ref: MatDialogRef<unknown>): void {
    const handle = this.register(
      () => {
        if (ref.disableClose) {
          return 'kept';
        }
        ref.close();
        return ref.getState() === MatDialogState.OPEN ? 'kept' : 'closed';
      },
      'mat-dialog',
      () => ref.getState() === MatDialogState.OPEN
    );
    ref.beforeClosed().subscribe(() => handle.release());
    ref.afterClosed().subscribe(() => handle.release());
  }

  // ------------------------------------------------------------ internals

  /**
   * Chrome ("history manipulation intervention") and WebKit mark the entry we
   * push FROM as skip-on-back when the push has no user activation. Back would
   * then jump over the notebook page for good. So we push only while the user
   * has just tapped or typed; an overlay that opened on its own (panel restored
   * from a preference, inactivity lock, update prompt) gets its entry on the
   * next tap, and until then back keeps its old meaning.
   */
  protected hasUserActivation(): boolean {
    const ua = (navigator as unknown as { userActivation?: { isActive: boolean } }).userActivation;
    return !!ua && ua.isActive;
  }

  private canPush(): boolean {
    return this.inGesture || this.hasUserActivation();
  }

  private armGesture(): void {
    if (this.gestureArmed) {
      return;
    }
    this.gestureArmed = true;
    ACTIVATION_EVENTS.forEach((type) => window.addEventListener(type, this.onGesture, true));
  }

  private disarmGesture(): void {
    if (!this.gestureArmed) {
      return;
    }
    this.gestureArmed = false;
    ACTIVATION_EVENTS.forEach((type) => window.removeEventListener(type, this.onGesture, true));
  }

  private readonly onGesture = (event: Event): void => {
    // Synthetic events grant no activation; Escape does not either.
    if (!event.isTrusted || (event instanceof KeyboardEvent && event.key === 'Escape')) {
      return;
    }
    this.disarmGesture();
    this.inGesture = true;
    try {
      this.sync();
    } finally {
      this.inGesture = false;
    }
  };

  private release(entry: Entry): void {
    if (entry.released) {
      return;
    }
    entry.released = true;
    this.stack = this.stack.filter((e) => e !== entry);
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
    }
    this.releaseTimer = setTimeout(() => {
      this.releaseTimer = null;
      this.sync();
    }, RELEASE_GRACE_MS);
  }

  private prune(): void {
    const dead = this.stack.filter((e) => e.isAlive && !safeAlive(e));
    if (dead.length === 0) {
      return;
    }
    dead.forEach((e) => (e.released = true));
    this.stack = this.stack.filter((e) => !e.released);
  }

  private isOurs(stamp: Stamp): boolean {
    return this.page !== null && stamp.page === this.page;
  }

  /** Take over the page the current entry belongs to (route change, reload). */
  private adoptCurrentEntry(cur: Stamp): void {
    if (cur.page === this.page) {
      return;
    }
    this.page = cur.page;
    this.seqs = [];
    if (cur.page !== null && cur.seq !== null) {
      this.seqs[cur.depth] = cur.seq;
    }
  }

  /** Make the current entry's depth equal the number of open overlays. */
  private sync(): void {
    if (!this.enabled || this.traversal || this.resolving > 0 || this.navigating) {
      return;
    }
    this.prune();
    const cur = stampOf(history.state);
    this.adoptCurrentEntry(cur);
    const desired = this.stack.length;
    const actual = cur.depth;

    if (actual < desired) {
      if (!this.canPush()) {
        this.armGesture();
      } else {
        this.disarmGesture();
        this.push(actual, desired);
      }
    } else if (actual > desired) {
      this.traverse('sync', desired - actual, { depth: desired, page: this.page, seq: this.seqs[desired] ?? null });
    } else {
      this.disarmGesture();
    }
    this.updateScrollRestoration();
  }

  private push(from: number, to: number): void {
    if (this.page === null) {
      // Stamp the page entry so a pop back onto it is recognisably ours.
      this.page = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      const seq = ++this.seqCounter;
      history.replaceState({ ...baseState(), [BACK_PAGE_KEY]: this.page, [BACK_SEQ_KEY]: seq }, '');
      this.seqs = [seq];
    }
    const base = baseState();
    for (let depth = from + 1; depth <= to; depth++) {
      const seq = ++this.seqCounter;
      history.pushState({ ...base, [BACK_STATE_KEY]: depth, [BACK_PAGE_KEY]: this.page, [BACK_SEQ_KEY]: seq }, '');
      this.seqs[depth] = seq;
    }
    this.seqs.length = to + 1; // anything above was pruned by the push
  }

  private traverse(kind: TraversalKind, delta: number, expect: Stamp | null, fallback = 0): void {
    if (this.traversal?.timer) {
      clearTimeout(this.traversal.timer);
    }
    const t: Traversal = {
      kind, delta, expect, fallback,
      from: stampOf(history.state),
      fromHref: location.href,
      waits: 0,
      hijacked: false,
      timer: null,
    };
    this.traversal = t;
    this.armTraversalTimer(t);
    history.go(delta);
  }

  /**
   * Never re-issues the go(): if the browser is merely slow, a second go()
   * would step the user one page too far. While nothing has moved we keep
   * waiting, then give up; a probe with nothing ahead falls back instead.
   */
  private armTraversalTimer(t: Traversal): void {
    t.timer = setTimeout(() => {
      t.timer = null;
      if (this.traversal !== t) {
        return;
      }
      const now = stampOf(history.state);
      const moved = location.href !== t.fromHref || now.page !== t.from.page || now.seq !== t.from.seq || now.depth !== t.from.depth;
      if (moved) {
        // We moved and missed the event: just stand down.
        this.traversal = null;
        return;
      }
      if (t.kind === 'probe') {
        this.traversal = null;
        this.traverse('sync', t.fallback, { depth: now.depth + t.fallback, page: this.page, seq: this.seqs[now.depth + t.fallback] ?? null });
        return;
      }
      if (++t.waits < TRAVERSAL_MAX_WAITS) {
        this.armTraversalTimer(t);
        return;
      }
      this.traversal = null;
    }, t.kind === 'probe' ? PROBE_TIMEOUT_MS : TRAVERSAL_TIMEOUT_MS);
  }

  private matches(stamp: Stamp, expect: Stamp | null): boolean {
    if (!expect) {
      return true;
    }
    return stamp.page === expect.page && stamp.depth === expect.depth && (expect.seq === null || stamp.seq === expect.seq);
  }

  private readonly onPopState = (event: PopStateEvent): void => {
    const stamp = stampOf(event.state);
    const t = this.traversal;

    if (t) {
      if (t.timer) {
        clearTimeout(t.timer);
      }
      this.traversal = null;
      if (t.kind === 'compensate') {
        event.stopImmediatePropagation();
        this.sync();
        return;
      }
      if (t.kind === 'probe') {
        if (this.isOurs(stamp)) {
          event.stopImmediatePropagation();
          this.sync();
        }
        // Forward past our stale entry onto a real page: the router takes it.
        return;
      }
      if (this.matches(stamp, t.expect)) {
        event.stopImmediatePropagation();
        this.sync();
        return;
      }
      if (t.hijacked) {
        // The router pushed its entry first and our go() walked off it. Undo.
        event.stopImmediatePropagation();
        this.traverse('compensate', -t.delta, null);
        return;
      }
      // A user back/forward overtook our queued go(). Handle this pop as theirs
      // and undo ours when it lands.
      this.orphan = { delta: t.delta, until: Date.now() + ORPHAN_WINDOW_MS };
    } else if (this.orphan) {
      const o = this.orphan;
      this.orphan = null;
      if (Date.now() <= o.until) {
        event.stopImmediatePropagation();
        this.traverse('compensate', -o.delta, null);
        return;
      }
    }

    this.prune();
    const open = this.stack.length;

    if (!this.isOurs(stamp)) {
      // A different page entry: ordinary back/forward. Overlays that survive the
      // route change get a fresh entry after NavigationEnd (on the next tap).
      return;
    }

    event.stopImmediatePropagation();

    if (this.releaseTimer && stamp.depth === open) {
      // Back pressed inside the close grace: it did our cleanup for us.
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
      this.updateScrollRestoration();
      return;
    }

    if (stamp.depth < open) {
      this.zone.run(() => this.backOut(open - stamp.depth));
    } else if (stamp.depth > open) {
      // Forward onto a stale entry: keep going if there is a page beyond it,
      // otherwise step back to where we belong.
      this.traverse('probe', 1, null, open - stamp.depth);
    } else {
      this.sync();
    }
  };

  /** The user popped `count` overlay entries: close that many, topmost first. */
  private backOut(count: number): void {
    // Non-modal overlays leave the page scrollable; the pop must not move it.
    const scrollY = window.scrollY;
    let pending = false;
    for (let i = 0; i < count; i++) {
      this.prune();
      const top = this.stack[this.stack.length - 1];
      if (!top) {
        break;
      }
      let outcome: BackOutcome;
      try {
        outcome = top.onBack();
      } catch (err) {
        console.error('Back button overlay handler failed', top.label, err);
        outcome = 'kept';
      }
      if (outcome === 'closed') {
        top.released = true;
        this.stack = this.stack.filter((e) => e !== top);
        continue;
      }
      pending = outcome === 'pending';
      break;
    }

    const finish = () => {
      if (window.scrollY !== scrollY) {
        window.scrollTo(window.scrollX, scrollY);
      }
      this.restoreAfterBack(count);
    };
    if (pending) {
      this.resolving++;
      setTimeout(() => {
        this.resolving--;
        finish();
      }, 0);
    } else {
      finish();
    }
  }

  /**
   * Overlays that refused to close lost their entry to the pop. Walk forward
   * onto the entries we came from rather than pushing new ones (a push here
   * has no user activation; see hasUserActivation).
   */
  private restoreAfterBack(popped: number): void {
    if (this.resolving > 0) {
      return;
    }
    this.prune();
    const cur = stampOf(history.state);
    const missing = this.stack.length - cur.depth;
    if (missing > 0 && !this.navigating) {
      const steps = Math.min(missing, popped);
      const depth = cur.depth + steps;
      this.traverse('restore', steps, { depth, page: this.page, seq: this.seqs[depth] ?? null });
      return;
    }
    this.sync();
  }

  /** While our entries exist, the browser must not restore scroll on their pops. */
  private updateScrollRestoration(): void {
    if (!('scrollRestoration' in history)) {
      return;
    }
    const want: ScrollRestoration = this.stack.length > 0 || overlayDepth(history.state) > 0 ? 'manual' : 'auto';
    if (history.scrollRestoration !== want) {
      history.scrollRestoration = want;
    }
  }
}

/** Registers on open and releases on close, driven by a boolean. */
export class BackSlot {
  private handle: BackEntryHandle | null = null;

  constructor(private svc: BackButtonDialogService, private onBack: BackHandler, private label: string) {}

  get active(): boolean {
    return !!this.handle && this.handle.active;
  }

  set(open: boolean): void {
    if (this.handle && !this.handle.active) {
      this.handle = null; // dropped by a back press
    }
    if (open && !this.handle) {
      this.handle = this.svc.register(this.onBack, this.label);
    } else if (!open && this.handle) {
      const handle = this.handle;
      this.handle = null;
      handle.release();
    }
  }
}

function safeAlive(entry: Entry): boolean {
  try {
    return entry.isAlive ? entry.isAlive() : true;
  } catch {
    return false;
  }
}

/** A hook failure must never break the dialog that triggered it. */
function report(fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.error('Back button hook failed', err);
  }
}

// -----------------------------------------------------------------------------
// Prototype hooks. ejs-dialog is used in ~100 templates across ~77 NgModules, so
// a directive would have to be declared in every one of them. Every Syncfusion
// dialog (and DialogUtility confirm/alert) is a Dialog instance, so the one
// central place to observe open/close is Dialog.prototype. show()/hide() are
// synchronous in Angular (ComponentBase.trigger calls its callback inline), so
// dialogOpen before/after tells us whether the call really opened or closed it
// (a beforeOpen/beforeClose handler may have cancelled). If a future version
// made trigger async, dialogOpen would read false after show() and no entry
// would be pushed: that fails safe.
// -----------------------------------------------------------------------------

const HOOKED = '__fnBackButtonHooked';

function installSyncfusionHook(): void {
  const proto = Dialog.prototype as any;
  if (!proto || proto[HOOKED]) {
    return;
  }
  proto[HOOKED] = true;
  const show = proto.show;
  const hide = proto.hide;
  const destroy = proto.destroy;
  proto.show = function (this: any, ...args: unknown[]) {
    const wasOpen = !!this.dialogOpen;
    const result = show.apply(this, args);
    if (!wasOpen && this.dialogOpen) {
      report(() => activeService?.onSyncfusionOpened(this));
    }
    return result;
  };
  proto.hide = function (this: any, ...args: unknown[]) {
    const wasOpen = !!this.dialogOpen;
    const result = hide.apply(this, args);
    if (wasOpen && !this.dialogOpen) {
      report(() => activeService?.onSyncfusionClosed(this));
    }
    return result;
  };
  proto.destroy = function (this: any, ...args: unknown[]) {
    report(() => activeService?.onSyncfusionClosed(this));
    return destroy.apply(this, args);
  };
}

function installMatDialogHook(): void {
  const proto = MatDialog.prototype as any;
  if (!proto || proto[HOOKED] || typeof proto.open !== 'function') {
    return;
  }
  proto[HOOKED] = true;
  const open = proto.open;
  proto.open = function (this: unknown, ...args: unknown[]) {
    const ref = open.apply(this, args);
    if (ref) {
      report(() => activeService?.trackMatDialog(ref));
    }
    return ref;
  };
}
